import React, { useEffect, useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import moment from "moment";

const ApplicationSetting = () => {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [settings, setSettings] = useState([]);
    const [editedSettings, setEditedSettings] = useState([]);

    const [addDialog, setAddDialog] = useState(false);
    const [addDialogBranches, setAddDialogBranches] = useState([]);
    const [settingTypeRecords, setSettingTypeRecords] = useState([]);
    const [addForm, setAddForm] = useState({
        clientid: null,
        branchid: null,
        settingTypeId: null,
        settingType: '',
        settingName: '',
        settingTitle: '',
        settingAccess: null,
        settingStatus: 'Active',
        effectiveFrom: null,
        dataTransfer: ''
    });
    const [addErrors, setAddErrors] = useState({});

    const [globalFilter, setGlobalFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        clientid: null,
        branchid: null,
        platformid: null,
    });

    const formatDate = (dateString) => {
        return moment(dateString).isValid()
            ? moment(dateString).format("DD-MM-YYYY")
            : "";
    };

    const statusOptions = [
        { label: "Active", value: "Active" },
        { label: "InActive", value: "InActive" },
    ];

    const accessOptions = [
        { label: "0", value: 0 },
        { label: "1", value: 1 },
        { label: "2", value: 2 },
        { label: "3", value: 3 },
    ];

    const dataTransferOptions = [
        { label: 'Updated', value: 'updated' },
        { label: 'Pending', value: 'pending' }
    ];

    useEffect(() => {
        fetchClients();
        fetchPlatforms();
        fetchSettings();
        fetchSettingTypeRecords();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
            setClients(
                res.data.map((c) => ({ label: c.client_name, value: c.client_id }))
            );
        } catch {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to load clients",
            });
        }
    };

    const fetchBranches = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', { params: { clientid: clientId } });
            setBranches(
                res.data.map((b) => ({ label: b.branch_name, value: b.branch_id }))
            );
        } catch {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to load branches",
            });
        }
    };

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms(
                (res.data || []).map((p) => ({
                    label: p.platform_name,
                    value: String(p.platform_id),
                }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    const fetchSettingTypeRecords = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/setting');
            setSettingTypeRecords(res.data || []);
        } catch (err) {
            console.error('Error fetching setting types:', err);
        }
    };

    const fetchAddDialogBranches = async (clientId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', { params: { clientid: clientId } });
            setAddDialogBranches((res.data || []).map((b) => ({ label: b.branch_name, value: b.branch_id })));
        } catch (err) {
            console.error('Error fetching branches:', err);
        }
    };

    const handleSettingTypeSelect = (settingId) => {
        const selected = settingTypeRecords.find((s) => s.setting_id === settingId);
        setAddForm((prev) => ({
            ...prev,
            settingTypeId: settingId,
            settingType: selected?.setting_name || '',
            settingName: selected?.setting_name || '',
            settingTitle: selected?.setting_title || selected?.setting_name || ''
        }));
        if (addErrors.settingTypeId)
            setAddErrors((e) => {
                const n = { ...e };
                delete n.settingTypeId;
                return n;
            });
    };

    const validateAddForm = () => {
        const errs = {};
        if (!addForm.clientid) errs.clientid = 'Client is required';
        if (!addForm.branchid) errs.branchid = 'Branch is required';
        if (!addForm.settingTypeId) errs.settingTypeId = 'Setting Type is required';
        if (addForm.settingAccess === null || addForm.settingAccess === '') errs.settingAccess = 'Setting Access is required';
        setAddErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const saveNewSetting = async () => {
        if (!validateAddForm()) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fix the highlighted fields' });
            return;
        }
        try {
            await axios.post(
                'https://treatway.co.in/Counter-Backend/api/server_masters/save/application_setting',
                JSON.stringify({
                    clientid: addForm.clientid,
                    branchid: addForm.branchid,
                    settingName: addForm.settingName,
                    settingAccess: addForm.settingAccess,
                    settingType: addForm.settingType,
                    settingTitle: addForm.settingTitle,
                    settingStatus: addForm.settingStatus,
                    effectiveFrom: addForm.effectiveFrom ? moment(addForm.effectiveFrom).format('YYYY-MM-DD') : '',
                    dataTransfer: addForm.dataTransfer
                }),
                { headers: { 'Content-Type': 'application/json' } }
            );
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Setting added successfully' });
            setAddDialog(false);
            setAddForm({ clientid: null, branchid: null, settingTypeId: null, settingType: '', settingName: '', settingTitle: '', settingAccess: null, settingStatus: 'Active', effectiveFrom: null, dataTransfer: '' });
            setAddErrors({});
            setAddDialogBranches([]);
            fetchSettings();
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to add setting' });
        }
    };

    const fetchSettings = async (appliedFilters = filters) => {
        setLoading(true);
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/application_setting', { params: appliedFilters });

            if (res.data && res.data.status === "success" && Array.isArray(res.data.settings)) {
                setSettings(res.data.settings);
                setEditedSettings(res.data.settings.map((s) => ({ ...s })));
            } else {
                setSettings([]);
                setEditedSettings([]);
            }
        } catch (err) {
            console.error("Error fetching settings:", err);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to load settings",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };

        if (key === "clientid") {
            fetchBranches(value);
            newFilters.branchid = null;
        }

        setFilters(newFilters);
        fetchSettings(newFilters);
    };

    const handleResetFilters = () => {
        setFilters({ clientid: null, branchid: null, platformid: null });
        setBranches([]);
        fetchSettings({ clientid: null, branchid: null, platformid: null });
    };

    //Editing
    const onCellEditChange = (rowIndex, field, value) => {
        const updated = [...editedSettings];
        updated[rowIndex] = { ...updated[rowIndex], [field]: value };
        setEditedSettings(updated);
    };

    const textEditor = (options) => {
        const row = editedSettings[options.rowIndex];
        return (
            <InputText
                type="text"
                value={row?.[options.field] || ""}
                onChange={(e) =>
                    onCellEditChange(options.rowIndex, options.field, e.target.value)
                }
                className="w-full"
            />
        );
    };

    const statusEditor = (options) => {
        const row = editedSettings[options.rowIndex];
        return (
            <Dropdown
                value={row?.[options.field]}
                options={statusOptions}
                onChange={(e) =>
                    onCellEditChange(options.rowIndex, options.field, e.value)
                }
                placeholder="Select Status"
                className="w-full"
            />
        );
    };

    const accessEditor = (options) => {
        const row = editedSettings[options.rowIndex];
        return (
            <Dropdown
                value={row?.[options.field]}
                options={accessOptions}
                onChange={(e) =>
                    onCellEditChange(options.rowIndex, options.field, e.value)
                }
                placeholder="Select Access"
                className="w-full"
            />
        );
    };

    const dateEditor = (options) => {
        const row = editedSettings[options.rowIndex];
        return (
            <InputText
                type="date"
                value={
                    row?.[options.field]
                        ? moment(row[options.field]).format("YYYY-MM-DD")
                        : ""
                }
                onChange={(e) =>
                    onCellEditChange(options.rowIndex, options.field, e.target.value)
                }
                className="w-full"
            />
        );
    };

    //Save
    const handleSaveAll = async () => {
        const changes = editedSettings.filter((row, idx) =>
            JSON.stringify(row) !== JSON.stringify(settings[idx])
        );

        if (changes.length === 0) {
            toast.current.show({
                severity: "info",
                summary: "No Changes",
                detail: "Nothing to update",
            });
            return;
        }

        try {
            await Promise.all(
                changes.map((row) =>
                    axios.post(
                        'https://treatway.co.in/Counter-Backend/api/server_masters/save/application_setting',
                        JSON.stringify({
                            id: row.app_setting_id,
                            settingName: row.setting_name,
                            settingAccess: row.setting_access,
                            settingType: row.setting_type,
                            settingTitle: row.setting_title,
                            settingStatus: row.setting_status,
                            effectiveFrom: row.effective_from
                        }),
                        { headers: { 'Content-Type': 'application/json' } }
                    )
                )
            );

            setSettings(editedSettings.map((s) => ({ ...s })));

            toast.current.show({
                severity: "success",
                summary: "Saved",
                detail: "All changes saved successfully",
            });
        } catch (err) {
            console.error("Save failed:", err);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to save some changes",
            });
        }
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Application Settings List</h2>

            <div className="card">
                <div className="flex flex-wrap gap-4 mt-3 align-items-end">
                    <div className="flex flex-column" style={{ flex: 1, minWidth: '180px' }}>
                        <label className="mb-1 font-medium">Client</label>
                        <Dropdown value={filters.clientid} options={clients} onChange={(e) => handleFilterChange('clientid', e.value)} placeholder="Select Client" className="w-full" filter />
                    </div>
                    <div className="flex flex-column" style={{ flex: 1, minWidth: '180px' }}>
                        <label className="mb-1 font-medium">Branch</label>
                        <Dropdown value={filters.branchid} options={branches} onChange={(e) => handleFilterChange('branchid', e.value)} placeholder="Select Branch" className="w-full" filter />
                    </div>
                    <div className="flex flex-column" style={{ flex: 1, minWidth: '180px' }}>
                        <label className="mb-1 font-medium">Platform</label>
                        <Dropdown value={filters.platformid} options={platforms} onChange={(e) => handleFilterChange('platformid', e.value)} placeholder="Select Platform" className="w-full" filter />
                    </div>
                    <div className="flex flex-column" style={{ flex: 1, minWidth: '180px' }}>
                        <label className="mb-1 font-medium"></label>
                        <Button label="Reset Filters" icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} />
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <Button
                            label="Add Setting"
                            icon="pi pi-plus"
                            onClick={() => {
                                setAddForm({ clientid: null, branchid: null, settingTypeId: null, settingType: '', settingName: '', settingTitle: '', settingAccess: null, settingStatus: 'Active', effectiveFrom: null, dataTransfer: '' });
                                setAddErrors({});
                                setAddDialogBranches([]);
                                setAddDialog(true);
                            }}
                        />
                    </div>
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={editedSettings}
                    showGridlines
                    paginator
                    rows={100}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm relative"
                    loading={loading}
                    loadingIcon={
                        <div className="flex align-items-center justify-content-center w-full h-full bg-black-alpha-30 absolute top-0 left-0">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".7s" />
                        </div>
                    }
                    globalFilter={globalFilter}
                    emptyMessage="No settings found"
                    editMode="cell"
                >
                    <Column field="client_name" header="Client" style={{ width: '120px' }} />
                    <Column field="branch_name" header="Branch" style={{ width: '120px' }} />
                    <Column field="setting_name" header="Setting Name" editor={textEditor} />
                    <Column field="setting_access" header="Setting Access" editor={accessEditor} style={{ width: '120px' }} />
                    <Column field="setting_type" header="Setting Type" editor={textEditor} />
                    <Column field="setting_title" header="Setting Title" editor={textEditor} />
                    <Column
                        field="setting_status"
                        header="Status"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.setting_status === 'Active' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.setting_status}</span>}
                        editor={statusEditor}
                    />
                    <Column field="effective_from" header="Effective From" body={(rowData) => formatDate(rowData.effective_from)} editor={dateEditor} style={{ width: '140px' }} />
                    <Column
                        field="data_transfer"
                        header="Data Transfer"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.data_transfer === 'updated' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.data_transfer}</span>}
                    />
                </DataTable>

                <div className="mt-3">
                    <Button label="Save All Changes" icon="pi pi-check" className="p-button-success" onClick={handleSaveAll} />
                </div>
            </div>

            {/* Add Setting Dialog */}
            <Dialog
                header="Add Application Setting"
                visible={addDialog}
                style={{ width: '550px', maxWidth: '95vw' }}
                modal
                draggable={false}
                resizable={false}
                onHide={() => {
                    setAddDialog(false);
                    setAddErrors({});
                }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setAddDialog(false);
                                setAddErrors({});
                            }}
                        />
                        <Button label="Save" icon="pi pi-check" className="p-button-success" onClick={saveNewSetting} />
                    </div>
                }
            >
                <div className="grid formgrid p-fluid">
                    {/* Client */}
                    <div className="col-12">
                        <div className="field">
                            <label>
                                Client <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                value={addForm.clientid}
                                options={clients}
                                onChange={(e) => {
                                    setAddForm((prev) => ({ ...prev, clientid: e.value, branchid: null }));
                                    fetchAddDialogBranches(e.value);
                                    if (addErrors.clientid)
                                        setAddErrors((err) => {
                                            const n = { ...err };
                                            delete n.clientid;
                                            return n;
                                        });
                                }}
                                placeholder="Select Client"
                                filter
                                className={`w-full ${addErrors.clientid ? 'p-invalid' : ''}`}
                            />
                            {addErrors.clientid && <small className="p-error">{addErrors.clientid}</small>}
                        </div>
                    </div>

                    {/* Branch */}
                    <div className="col-12">
                        <div className="field">
                            <label>
                                Branch <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                value={addForm.branchid}
                                options={addDialogBranches}
                                onChange={(e) => {
                                    setAddForm((prev) => ({ ...prev, branchid: e.value }));
                                    if (addErrors.branchid)
                                        setAddErrors((err) => {
                                            const n = { ...err };
                                            delete n.branchid;
                                            return n;
                                        });
                                }}
                                placeholder="Select Branch"
                                filter
                                disabled={!addForm.clientid}
                                className={`w-full ${addErrors.branchid ? 'p-invalid' : ''}`}
                            />
                            {addErrors.branchid && <small className="p-error">{addErrors.branchid}</small>}
                        </div>
                    </div>

                    {/* Setting Type */}
                    <div className="col-12">
                        <div className="field">
                            <label>
                                Setting Type <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                value={addForm.settingTypeId}
                                options={settingTypeRecords.map((s) => ({ label: s.setting_name, value: s.setting_id }))}
                                onChange={(e) => handleSettingTypeSelect(e.value)}
                                placeholder="Select Setting Type"
                                filter
                                className={`w-full ${addErrors.settingTypeId ? 'p-invalid' : ''}`}
                            />
                            {addErrors.settingTypeId && <small className="p-error">{addErrors.settingTypeId}</small>}
                        </div>
                    </div>

                    {/* Setting Name (auto-filled) */}
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>Setting Name</label>
                            <InputText value={addForm.settingName} onChange={(e) => setAddForm((prev) => ({ ...prev, settingName: e.target.value }))} placeholder="Auto-filled from Setting Type" className="w-full" />
                        </div>
                    </div>

                    {/* Setting Title (auto-filled) */}
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>Setting Title</label>
                            <InputText value={addForm.settingTitle} onChange={(e) => setAddForm((prev) => ({ ...prev, settingTitle: e.target.value }))} placeholder="Auto-filled from Setting Type" className="w-full" />
                        </div>
                    </div>

                    {/* Setting Access (mandatory) */}
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>
                                Setting Access <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                value={addForm.settingAccess}
                                options={accessOptions}
                                onChange={(e) => {
                                    setAddForm((prev) => ({ ...prev, settingAccess: e.value }));
                                    if (addErrors.settingAccess)
                                        setAddErrors((err) => {
                                            const n = { ...err };
                                            delete n.settingAccess;
                                            return n;
                                        });
                                }}
                                placeholder="Select Access"
                                className={`w-full ${addErrors.settingAccess ? 'p-invalid' : ''}`}
                            />
                            {addErrors.settingAccess && <small className="p-error">{addErrors.settingAccess}</small>}
                        </div>
                    </div>

                    {/* Status */}
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>Status</label>
                            <Dropdown value={addForm.settingStatus} options={statusOptions} onChange={(e) => setAddForm((prev) => ({ ...prev, settingStatus: e.value }))} placeholder="Select Status" className="w-full" />
                        </div>
                    </div>

                    {/* Effective From + Data Transfer — single row */}
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>Effective From</label>
                            <Calendar value={addForm.effectiveFrom} onChange={(e) => setAddForm((prev) => ({ ...prev, effectiveFrom: e.value }))} dateFormat="dd-mm-yy" showIcon placeholder="DD-MM-YYYY" className="w-full" />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>Data Transfer</label>
                            <Dropdown value={addForm.dataTransfer} options={dataTransferOptions} onChange={(e) => setAddForm((prev) => ({ ...prev, dataTransfer: e.value }))} placeholder="Select Data Transfer" className="w-full" />
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ApplicationSetting;
